import * as d3 from "d3";
import * as _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Button, Segment, Table } from "semantic-ui-react";
import { PlotChannelColors, PlotChannelDeepColors } from "../../models/common_model";
import { LimitData, Measurement, MeasurementData } from "../../models/testing_model";

export function ScalarPlotComponent(props: {
    measurement: Measurement,
    data: MeasurementData[]
}) {
    const currentInfo = useState<{
        value: number,
        isShow: boolean
    }>({ value: 0, isShow: false })

    const elementRef = useRef<HTMLDivElement>(null);

    const { measurement, data: allData } = props;

    const [data, setData] = useState<MeasurementData[]>(allData);

    const channels = _.uniq(allData.map(d => d.channel)).sort()
    const previousIndexes = _.uniq(allData.map(d => d.index)).sort();

    const currentAttempts = useState(_.max(previousIndexes));

    useEffect(() => {
        setData(allData.filter(d => d.index === currentAttempts[0]));
    }, [allData, currentAttempts[0]])

    useEffect(() => {
        const width = elementRef.current!.offsetWidth;
        const height = 250 * channels.length;

        const margin = ({ top: 20, right: 230, bottom: 40, left: 50 })

        const svg = d3.select(elementRef.current!)
            .append("svg")
            .attr("viewBox", [0, 0, width, height] as any);

        let allNumbers: number[] = []

        for (const d of data) {
            allNumbers.push(d.result as number);
            if (_.isNumber(d.limits.lower)) {
                allNumbers.push(d.limits.lower as number);
            }
            if (_.isNumber(d.limits.upper)) {
                allNumbers.push(d.limits.upper as number);
            }
        }
        
        const xDomain = d3.extent(allNumbers.filter(_.isNumber).map(v => v)) as number[];

        const xDomainEdge = (xDomain[1] - xDomain[0]) / 10;
        xDomain[0] -= xDomainEdge;
        xDomain[1] += xDomainEdge;

        const x = d3.scaleLinear().domain(xDomain).range([margin.left, width - margin.right]);

        const channelLabels = channels.map(i => `CH${i}`);

        //const yDomain = d3.extent([0, channels.length * BAR_HEIGHT * 2]) as number[];
        const y = d3.scaleBand().domain(channelLabels).range([margin.top, height - margin.bottom]);
        const yAxis = (g: any) => {
            g.attr("transform", `translate(${margin.left},0)`).call(d3.axisLeft(y as any));
        }

        svg.append("g")
            .attr("class", "y-axis")
            .call(yAxis);

        const valueHeight = channelLabels.length > 1 ? ((y(channelLabels[channelLabels.length - 1]) as number) - (y(channelLabels[0]) as number)) / channelLabels.length : (height - margin.bottom - margin.top) / 2;
        const xLeft = x(xDomain[1]);
        let valueStartX = x(xDomain[0]);

        const channelXValues = data.map((d) => {
            return x(d.result as number) as number;
        });
        const miniChannelXValue = _.min(channelXValues) as number;
        
        svg.append('line')
            .attr('class', 'value-line')
            .attr("fill", "none")
            .attr("stroke", '#000')
            .attr("stroke-width", '2px')
            .attr("x1", valueStartX - 1)
            .attr("y1", margin.top)
            .attr("x2", valueStartX - 1)
            .attr("y2", height - margin.bottom);

        data.forEach((channelData, channelIndex) => {
            const value = channelData.result as number;
            const limits = channelData.limits as LimitData<number>;
            const d = data.filter(d => d.channel === channelIndex)[0];
            const lineY = y(`CH${channelData.channel}`) as number + valueHeight;

            svg.append("rect")
                .attr('class', 'value-bar')
                .attr("fill", PlotChannelColors[channelIndex])
                .attr("stroke", 'none')
                .attr('width', channelXValues[channelIndex] - valueStartX)
                .attr('height', valueHeight)
                .attr('x', valueStartX)
                .attr('y', lineY - valueHeight / 2);

            const squareSize = valueHeight / 8;
            const triangleSize = squareSize * 0.8;
            svg.append('line')
                .attr('class', 'value-line')
                .attr("fill", "none")
                .attr("stroke", '#999')
                .attr("stroke-width", '1px')
                .attr("x1", xLeft)
                .attr("y1", lineY)
                .attr("x2", x(xDomain[0]))
                .attr("y2", lineY);

            svg.append("rect")
                .attr('class', 'value-square')
                .attr("fill", PlotChannelColors[channelIndex])
                .attr("stroke", 'none')
                .attr('width', squareSize)
                .attr('height', squareSize)
                .attr('x', xLeft)
                .attr('y', lineY - squareSize / 2);

            svg.append('text')
                .attr('dominant-baseline', 'middle')
                .attr('x', xLeft + squareSize + 5)
                .attr('y', lineY)
                .style('line-height', squareSize + 'px')
                .text(`${value} ${measurement.result_unit}`);

            if (_.isNumber(limits.lower)) {
                const xLower = x(limits.lower);
                svg.append('polygon')
                    .attr('class', 'limit-triangle')
                    .attr("fill", PlotChannelDeepColors[channelIndex])
                    .attr("stroke", '#999')
                    .attr("points", [`${xLower - triangleSize},${lineY - triangleSize / 2}`, `${xLower - triangleSize},${lineY + triangleSize / 2}`, `${xLower},${lineY}`].join(" "));
            }

            if (_.isNumber(limits.upper)) {
                const xUpper = x(limits.upper);
                svg.append('polygon')
                    .attr('class', 'limit-triangle')
                    .attr("fill", PlotChannelDeepColors[channelIndex])
                    .attr("stroke", '#999')
                    .attr("points", [`${xUpper},${lineY}`, `${xUpper + triangleSize},${lineY + triangleSize / 2}`, `${xUpper + triangleSize},${lineY - triangleSize / 2}`].join(" "));
            }
        });

        const xAxis = (g: any) => {
            g.attr("transform", `translate(0,${height - margin.bottom})`)
                .call(d3.axisBottom(x as any).tickSizeInner(-height + margin.top + margin.bottom));
        }

        svg.append("g")
            .attr("class", "x-axis-inner")
            .call(xAxis);

        svg.append('text')
        .attr('class', 'axis-label')
        .attr('x', x(xDomain[0]) + x(xDomain[1])/2 - x(xDomain[0])/2 - 120)
        .attr('y', height)
        .text(measurement.measurement_name + (measurement.result_unit ? ` (${measurement.result_unit})` : ''))

        //focus
        const focus = svg.append('g').style('display', 'none');

        focus.append('line')
            .attr('id', 'focusLineX')
            .attr('class', 'focusLine');

        svg.append('rect')
            .attr('class', 'overlay')
            .attr('width', width)
            .attr('height', height)
            .on('mouseover', function () { focus.style('display', null); })
            .on('mouseout', function () {
                focus.style('display', 'none');
                currentInfo[1](info => ({ ...info, isShow: false }))
            })
            .on('mousemove', function (event) {
                const mouse = d3.pointer(event);
                const mouseX = x.invert(mouse[0]);

                if (mouseX < xDomain[0] || mouseX > xDomain[1]) {
                    currentInfo[1](v => ({
                        ...v,
                        isShow: false
                    }))
                } else {
                    currentInfo[1](v => ({
                        value: mouseX,
                        isShow: true
                    }))

                    const xv = x(mouseX);

                    focus.select('#focusLineX')
                        .attr('x1', xv).attr('y1', margin.top)
                        .attr('x2', xv).attr('y2', height - margin.bottom);
                }
            });

        return () => {
            svg.remove()
        }

    }, [data]);


    return <div>
        {previousIndexes.length > 1 && <>
            <Button.Group size="mini" compact>
                {previousIndexes.map(i => <Button key={i} size="mini" basic={currentAttempts[0] !== i} onClick={e => currentAttempts[1](i)}>Attempts {i}</Button>)}
            </Button.Group>
        </>}
        <div style={{ padding: '1rem 1rem 1rem 4rem' }}>
            <div style={{ visibility: currentInfo[0].isShow ? 'visible' : 'hidden' }}>
                value: {currentInfo[0].value.toFixed(2)}
            </div>
        </div>
        <div ref={elementRef} />
    </div>
}